import { useEffect, useRef, useState, type CSSProperties, type ReactElement } from 'react';
import { v2, parseApiErrorBody, type Document, type RelatedDocumentReview } from '@/api/v2Client';
import type { PriorDocument } from '../shared/LifecyclePriorDocPicker';

interface Props {
  document: Document;
  canSelect: boolean;
  onSelect: (prior: PriorDocument) => void;
  operation?: 'replace' | 'append' | 'delete';
}

const buttonStyle: CSSProperties = { padding: '5px 9px', border: '1px solid var(--border)', borderRadius: 4, background: 'var(--surface)', color: 'var(--text)', font: 'inherit' };

/** Evidence only: choosing a match stages a target in the operation editor. */
export function RelatedDocumentReviewPanel({ document, canSelect, onSelect, operation }: Props): ReactElement {
  const [result, setResult] = useState<RelatedDocumentReview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const request = useRef(0);
  const sourceRevision = document.RowVersion?.data?.map(b => b.toString(16).padStart(2, '0')).join('');

  async function load(offset = 0): Promise<void> {
    const token = ++request.current;
    setLoading(true);
    setError(null);
    if (offset === 0) setResult(null);
    try {
      const response = await v2.documents.getRelatedDocuments(document.Id, offset, 20, {
        ...(sourceRevision ? { sourceRevision } : {}),
        ...(offset > 0 && result?.historyToken ? { historyToken: result.historyToken } : {}),
        ...(operation ? { operation } : {}),
      });
      if (token !== request.current) return;
      if (!sourceRevision || response.sourceDocumentId.toLowerCase() !== document.Id.toLowerCase()
        || response.sourceRevision.toLowerCase() !== sourceRevision.toLowerCase()) {
        setResult(null);
        setError('This document changed. Reload the document before reviewing matches.');
        return;
      }
      setResult(previous => {
        if (offset === 0 || !previous) return response;
        const matches = new Map(previous.matches.map(match => [match.document.Id.toLowerCase(), match]));
        for (const match of response.matches) matches.set(match.document.Id.toLowerCase(), match);
        // Any extraction/history gaps from earlier pages remain visible.
        const reasons = [...new Set([...previous.reasons, ...response.reasons])];
        return { ...response, reasons, matches: [...matches.values()],
          coverage: { ...response.coverage, compared: previous.coverage.compared + response.coverage.compared },
          status: response.status === 'complete' && reasons.length > 0 ? 'incomplete' : response.status };
      });
    } catch (err) {
      if (token === request.current) setError(`${parseApiErrorBody(err)?.message ?? (err instanceof Error ? err.message : 'Could not compare earlier documents.')} Refresh matches to try again; reload the document if it changed.`);
    } finally {
      if (token === request.current) setLoading(false);
    }
  }

  useEffect(() => {
    // The backend owns readiness and returns not-checked before reading bytes.
    void load();
    return () => { request.current += 1; };
    // The parent keys this panel by document, revision, classification and intended operation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <section aria-label="Related documents review" style={{ width: '100%', borderTop: '1px solid var(--border)', paddingTop: 10 }}>
    <h3 style={{ fontSize: 13, margin: '0 0 6px' }}>Related documents in earlier sequences</h3>
    <p style={{ fontSize: 12, margin: '0 0 8px' }}>Review the evidence before choosing an operation. Matches do not change this document or earlier classifications.</p>
    <p style={{ fontSize: 12, margin: '0 0 8px' }}>Target eligibility shown for {(operation ?? 'replace').toUpperCase()}. Choose another operation above to check its targets.</p>
    <>
      <button type="button" style={buttonStyle} disabled={loading} onClick={() => { void load(); }}>Refresh matches</button>
      {loading && <p role="status">Comparing earlier documents…</p>}
      {error && <p role="alert">{error}</p>}
      {result && <>
        <p role="status" style={{ fontSize: 12 }}>
          {result.status === 'complete' ? 'Comparison complete.' : result.status === 'incomplete' ? 'Comparison incomplete.' : result.status === 'unavailable' ? 'Comparison unavailable.' : 'Comparison not checked.'}
          {' '}Compared {result.coverage.compared} of {result.coverage.total} documents; {result.coverage.remaining} remaining.
          {' '}Exact hashes checked: {result.coverage.exactHashesChecked}.
        </p>
        {result.reasons.length > 0 && <ul style={{ fontSize: 12 }}>{result.reasons.map(reason => <li key={reason}>{reason}</li>)}</ul>}
        {result.matches.length === 0 && result.status === 'complete' && !error && <p>No related documents found in the checked history.</p>}
        {result.matches.length === 0 && result.status !== 'complete' && <p>No matches are available from the comparisons completed so far.</p>}
        <ul style={{ listStyle: 'none', padding: 0, display: 'grid', gap: 8, maxHeight: 300, overflowY: 'auto' }}>
          {result.matches.map(match => <li key={match.document.Id} style={{ border: '1px solid var(--border)', borderRadius: 4, padding: 8, fontSize: 12 }}>
            <strong>{match.document.FileName}</strong>
            <div>Sequence {match.document.SubSequenceNumber ?? match.document.SequenceNumber ?? 'unknown'} · Section {match.document.CtdSectionCode ?? 'unknown'}</div>
            <div>{match.relationship.replaceAll('-', ' ')} · {match.state} · {match.trust}</div>
            {match.trust === 'provisional' && <div>Earlier draft; finalization is required before publishing.</div>}
            <ul>{match.evidence.map(evidence => <li key={evidence}>{evidence}</li>)}</ul>
            {match.blockers.length > 0 && <p>{match.blockers.join(' ')}</p>}
            <button type="button" style={buttonStyle} disabled={!canSelect || !match.eligible || loading || error !== null}
              onClick={() => onSelect(match.document)} aria-label={`Review ${match.document.FileName} as prior document`}>
              Review as prior document
            </button>
          </li>)}
        </ul>
        {result.nextOffset !== null && <button type="button" style={buttonStyle} disabled={loading || error !== null}
          onClick={() => { void load(result.nextOffset ?? 0); }}>Compare more documents</button>}
      </>}
    </>
  </section>;
}
